import { Collapsible } from '@base-ui/react/collapsible'
import { ChevronRight } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { NavUser } from '@/app/layout/NavUser'
import { useNavGroups } from '@/app/layout/nav-items'
import { Badge } from '@/components/ui/badge'
import { OverlayClose } from '@/components/ui/overlay-close'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
  useSidebar,
} from '@/components/ui/sidebar'
import { messages } from '@/lib/messages'
import { useCashTodoCount } from '@/features/cash/hooks'
import { useOpenTaskCount, useRequestCounts } from '@/features/hr/hr-actions'

/** İşlerim'deki açık iş sayısı; sıfırsa gösterilmez. Kalem yalnız yetkiliye görünür. */
function HrTasksBadge() {
  const count = useOpenTaskCount()
  if (!count) return null
  return (
    <Badge
      tone="warning"
      size="sm"
      className="tabular-nums"
      aria-label={messages.hr.tasks.count(count, count)}
    >
      {count}
    </Badge>
  )
}

/** Bu Hafta: seçili firmanın bekleyen iş maddesi sayısı; sıfırsa gösterilmez. */
function CashTodoBadge() {
  const count = useCashTodoCount()
  if (!count) return null
  return (
    <Badge
      tone="warning"
      size="sm"
      className="tabular-nums"
      aria-label={messages.nav.cashTodoCount(count)}
    >
      {count}
    </Badge>
  )
}

/** Onaylar: onayımı bekleyen (uyarı) ve açtığım revizyondaki talepler; sıfırsa gösterilmez. */
function HrApprovalsBadges() {
  const counts = useRequestCounts()
  if (!counts) return null
  return (
    <>
      {counts.awaiting_me > 0 && (
        <Badge
          tone="warning"
          size="sm"
          className="tabular-nums"
          aria-label={messages.nav.hrApprovalsAwaiting(counts.awaiting_me)}
        >
          {counts.awaiting_me}
        </Badge>
      )}
      {counts.my_revisions > 0 && (
        <Badge tone="info" size="sm" className="tabular-nums">
          {messages.nav.hrApprovalsRevisions(counts.my_revisions)}
        </Badge>
      )}
    </>
  )
}

export function AppSidebar() {
  const location = useLocation()
  const groups = useNavGroups()
  // Elle açılıp kapatılanlar burada; dokunulmamış grup yalnız aktif sayfanınsa açıktır.
  const [toggled, setToggled] = useState<Record<string, boolean>>({})
  const activeGroup = groups.find((g) => g.items.some((i) => i.to === location.pathname))?.label
  const { isMobile, setOpenMobile } = useSidebar()

  // Mobilde menü sayfanın üstünü örter: sayfa değişince (menü, parola, arama paleti) kapanır.
  useEffect(() => {
    setOpenMobile(false)
  }, [location.pathname, setOpenMobile])

  return (
    <Sidebar
      role="navigation"
      aria-label={messages.common.mainNavigation}
      className="top-(--header-height) h-[calc(100svh-var(--header-height))]!"
    >
      {/* Mobilde menü perdenin üstünde açılır ve üst çubuğu örter: kapatma düğmesi menünün içinde. */}
      {isMobile && (
        <SidebarHeader className="border-sidebar-border flex-row items-center justify-between border-b py-3 pr-3 pl-4">
          <img src="/betontas.png" alt={messages.app.name} className="h-6 w-auto" />
          <OverlayClose className="size-10" />
        </SidebarHeader>
      )}
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {groups.map((group) => (
                <Collapsible.Root
                  key={group.label}
                  open={toggled[group.label] ?? group.label === activeGroup}
                  onOpenChange={(open) => setToggled((prev) => ({ ...prev, [group.label]: open }))}
                  render={<SidebarMenuItem />}
                >
                  <Collapsible.Trigger render={<SidebarMenuButton className="max-md:h-11" />}>
                    <group.icon />
                    <span className="min-w-0 flex-1 truncate">{group.label}</span>
                    <span className="text-text-muted mr-1.5 text-[11px] font-[450] tabular-nums">
                      {group.items.length}
                    </span>
                    <ChevronRight className="text-text-muted group-hover/menu-button:text-foreground group-focus-visible/menu-button:text-foreground opacity-75 transition-[transform,color,opacity] duration-(--duration-spring) ease-(--ease-spring) group-hover/menu-button:opacity-100 group-focus-visible/menu-button:opacity-100 group-data-[panel-open]/menu-button:rotate-90 motion-reduce:transition-none" />
                  </Collapsible.Trigger>
                  {/* Yükseklik Base UI ölçümüyle (`--collapsible-panel-height`) yayla açılır, kapanırken hızlı solar. */}
                  <Collapsible.Panel className="h-(--collapsible-panel-height) overflow-hidden transition-[height,opacity] duration-(--duration-spring) ease-(--ease-spring) data-ending-style:h-0 data-ending-style:opacity-0 data-ending-style:duration-(--duration-fast) data-ending-style:ease-(--ease-standard) data-starting-style:h-0 data-starting-style:opacity-0 motion-reduce:transition-none">
                    <SidebarMenuSub>
                      {group.items.map((item) => (
                        <SidebarMenuSubItem key={item.to}>
                          <SidebarMenuSubButton
                            isActive={location.pathname === item.to}
                            render={<NavLink to={item.to} />}
                            className="max-md:h-10"
                            // Açık sayfaya dokunmak rota değiştirmez; menü yine kapansın.
                            onClick={() => setOpenMobile(false)}
                          >
                            <span className="min-w-0 flex-1 truncate">{item.label}</span>
                            {item.badge === 'hrTasks' && <HrTasksBadge />}
                            {item.badge === 'hrApprovals' && <HrApprovalsBadges />}
                            {item.badge === 'cashTodo' && <CashTodoBadge />}
                          </SidebarMenuSubButton>
                        </SidebarMenuSubItem>
                      ))}
                    </SidebarMenuSub>
                  </Collapsible.Panel>
                </Collapsible.Root>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  )
}
